import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { isPlaceholderAvatar } from '@/lib/avatar/startingAvatar'

export type UserRole = 'player' | 'verified-creator' | 'gm' | 'moderator' | 'admin'
export type Tier = 'free' | 'vault-basic' | 'vault-premium'

export interface UserProfile {
  id: string
  email: string
  displayName: string
  avatarUrl: string | null
  backstory: string | null
  isPublic: boolean
  roles: UserRole[]
  tier: Tier
  createdAt: string
}

interface UserState {
  user: UserProfile | null
  token: string | null
  status: 'anonymous' | 'authenticated' | 'loading'
  setSession: (user: UserProfile, token: string) => void
  updateProfile: (patch: Partial<UserProfile>) => void
  clear: () => void
  hasRole: (role: UserRole) => boolean
  isPremium: () => boolean
}

/**
 * Client-side user/session state. The token is a short-lived JWT issued by the
 * auth provider (Supabase/Clerk — see _docs/06-auth-and-users.md). Role/tier
 * gating here is UI-only; authoritative checks happen server-side.
 */
export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      status: 'anonymous',
      setSession: (user, token) =>
        set((s) => {
          // A session refresh rebuilds the profile from identity only; keep what
          // the player already chose for this account (portrait, display name,
          // backstory, visibility) instead of resetting it to the account's.
          const prev = s.user
          const same = !!prev && prev.id === user.id
          const kept = same
            ? {
                ...user,
                displayName: prev.displayName || user.displayName,
                backstory: prev.backstory,
                isPublic: prev.isPublic,
                avatarUrl: isPlaceholderAvatar(prev.avatarUrl) ? user.avatarUrl : prev.avatarUrl,
              }
            : user
          return {
            user: kept,
            token,
            status: 'authenticated',
          }
        }),
      updateProfile: (patch) => set((s) => (s.user ? { user: { ...s.user, ...patch } } : s)),
      clear: () => set({ user: null, token: null, status: 'anonymous' }),
      hasRole: (role) => !!get().user?.roles.includes(role),
      isPremium: () => {
        const t = get().user?.tier
        return t === 'vault-basic' || t === 'vault-premium'
      },
    }),
    { name: 'grymwyrd-user', partialize: (s) => ({ user: s.user, token: s.token, status: s.status }) },
  ),
)
